import React, { useState, useEffect, useRef, useCallback } from 'react';
import './IndicesPeerComparisonWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import Pagination from '../../Components/Common/Pagination/Pagination';
import {
  paginate,
  PaginationType,
  getMaxPageNum,
} from '../../utils/pagination';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import CustomSelect, {
  SelectOption,
} from '../../Components/Common/CustomSelect/CustomSelect';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import { formatNumber, formatKMBT } from '../../utils/utils';
import classNames from 'classnames';

import { getIndicesPeerCompare } from '../../api/company.api';
import { IndicesPeerComparisonDataType } from '../../types/indicespeercomparison.type';
import { ReactComponent as SortIcon } from '../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as SortAscIcon } from '../../assets/icons/arrow-up-s-fill.svg';
import { ReactComponent as SortableIcon } from '../../assets/icons/arrow-down-s-unfill.svg';
import {
  Region,
  ThemeType,
  MarketIndex,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

import Loading from '../../Components/Loading/Loading';

import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import { THEME_COLOR_MAPPING } from '../../utils/constant';
import { StockData } from '../../types/indices.type';
import { getUpDownClass } from '../../utils/utils';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
export interface IndicesPeerComparisonWidgetProps extends BaseWidgetProps {
  token?: string;
  region?: Region;
  index?: MarketIndex;
  tab?: string;
  onCallback?: (message: StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const IndicesPeerComparisonWidget = (
  props: IndicesPeerComparisonWidgetProps,
) => {
  const { onHeightChange } = props;
  const theme = props.theme ?? ThemeType.Direct;
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(
    widgetRef,
    WidgetNameSource.IndicesPeerComparison,
    onHeightChange,
  );
  const { width, isSmallScreen, isLargeScreen } = useContainerWidth(widgetRef);
  const msgItem = errorMsg.find((obj) => obj.key === 'peerComparison');
  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);
  const RESULTS_PER_PAGE = 10;
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>(
    paginate(page, RESULTS_PER_PAGE, total, maxPageNum),
  );

  const [data, setData] = useState<IndicesPeerComparisonDataType[]>([]);
  const [tab, setTab] = useState<string>(props.tab || 'narrow');
  const [index, setIndex] = useState<MarketIndex>(
    props.index || MarketIndex.XAO,
  );
  const [region, setRegion] = useState<Region>(props.region || Region.AU);

  const [currency, setCurrency] = useState('');
  const [sortcol, setSortcol] = useState(
    props.tab === 'summary'
      ? 'mktcap'
      : props.tab === 'performance'
        ? 'perf1Y'
        : props.tab === 'technical'
          ? 'avgvol30'
          : props.tab === 'fundamentals'
            ? 'mktcap'
            : props.tab === 'companyscore'
              ? 'overall'
              : props.tab === 'ratings'
                ? 'rating'
                : 'mktcap',
  );
  const [sortord, setSortord] = useState('desc');
  const [selcol, setSelcol] = useState('mktcap');
  const [isSorting, setIsSorting] = useState(false);

  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev: any) => ({ ...prev, show: false }));
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // ToolTip
  const peercomTooltipRef = useRef<HTMLDivElement | null>(null);
  const constrainTooltipPosition = (e: React.MouseEvent) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = peercomTooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = peercomTooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  function setToolTips(item: any, fid: string): any {
    if (isSmallScreen) {
      const handleClickName = (e: React.MouseEvent<HTMLSpanElement>) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const scrollX =
          window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;
        const x = rect.left + scrollX;
        const y = rect.bottom + 2 + scrollY;
        setTooltip({
          content: item.name,
          show: true,
          x,
          y,
        });
      };
      return (
        <div className="peer-com-tbl-cel-flex-container">
          <span className="peer-com-tbl-cel-text">{item.name}</span>
        </div>
      );
    }

    return (
      <div className="peer-com-tbl-cel-flex-container">
        <span
          className="peer-com-tbl-cel-text"
          onMouseEnter={(e) => {
            const constrainedPosition = constrainTooltipPosition(e);
            setTooltip({
              show: true,
              x: constrainedPosition.x,
              y: constrainedPosition.y,
              content: item.name,
            });
          }}
          onMouseMove={(e) => {
            //if no move, do not update tooltip
            if (e.movementX === 0 && e.movementY === 0) {
              return;
            }
            const constrainedPosition = constrainTooltipPosition(e);
            setTooltip((prev) => ({
              ...prev,
              x: constrainedPosition.x,
              y: constrainedPosition.y,
            }));
          }}
          onMouseLeave={() => {
            setTooltip((prev) => ({ ...prev, show: false }));
          }}
          onMouseOut={() => {
            setTooltip((prev) => ({ ...prev, show: false }));
          }}
        >
          {item.name}
        </span>
      </div>
    );
  }

  // update when props change
  useEffect(() => {
    setPage(1);
    if (props.tab) {
      setTab(props.tab);

      if (props.tab === 'summary') setSortcol('mktcap');
      else if (props.tab === 'performance') setSortcol('perf1Y');
      else if (props.tab === 'technical') setSortcol('avgvol30');
      else if (props.tab === 'fundamentals') setSortcol('mktcap');
      else if (props.tab === 'companyscore') setSortcol('overall');
      else if (props.tab === 'ratings') setSortcol('rating');
      else if (props.tab === 'narrow') setSortcol('mktcap');

      setSortord('desc');
    }
    if (props.index) {
      setIndex(props.index);
    }
    if (props.region) {
      setRegion(props.region);
    }
  }, [props.tab, props.index, props.region]);

  // Update pagination when total changes
  useEffect(() => {
    setPagination(paginate(page, RESULTS_PER_PAGE, total, maxPageNum));
  }, [total, page, maxPageNum]);

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, false, false, false));
    }
  }, [width]);

  useEffect(() => {
    if (isSmallScreen || isLargeScreen) {
      setTab('narrow');
    } else {
      if (props.tab) {
        setTab(props.tab);
      }
    }
  }, [isSmallScreen, isLargeScreen, tab]);

  // Initial state for reset functionality (palette: strong green → light green → neutral → light red → dark red)
  const initialHeatmapColor = [
    {
      color: '#085026',
      fontColor: '#ffffff',
      min: 5,
      max: 1000000,
      label: '+5%',
    },
    { color: '#11773C', fontColor: '#ffffff', min: 4, max: 5, label: '+4%' },
    { color: '#188647', fontColor: '#ffffff', min: 3, max: 4, label: '+3%' },
    { color: '#1C7D45', fontColor: '#ffffff', min: 2, max: 3, label: '+2%' },
    { color: '#99DDB6', fontColor: '#1E1F2F', min: 1, max: 2, label: '+1%' },
    { color: '#DFDFDF', fontColor: '#1E1F2F', min: -0.5, max: 1, label: '0%' },
    {
      color: '#FFBCBC',
      fontColor: '#1E1F2F',
      min: -1,
      max: -0.5,
      label: '-0.5%',
    },
    { color: '#F18989', fontColor: '#1E1F2F', min: -2, max: -1, label: '-1%' },
    { color: '#E73838', fontColor: '#ffffff', min: -3, max: -2, label: '-2%' },
    { color: '#D81F1F', fontColor: '#ffffff', min: -4, max: -3, label: '-3%' },
    { color: '#B21B1B', fontColor: '#ffffff', min: -5, max: -4, label: '-4%' },
    {
      color: '#9E1111',
      fontColor: '#ffffff',
      min: -1000000,
      max: -5,
      label: '-5%',
    },
  ];

  // Call API when page changes
  useEffect(() => {
    var tmpTab = tab;
    if (isSmallScreen || isLargeScreen) tmpTab = 'narrow';

    fetchRecommendationData(tmpTab);
  }, [page, props.token, tab, index, region, sortcol, sortord]);

  const fetchRecommendationData = useCallback(
    async (tmpTab: string) => {
      if (!isSorting) setIsLoading(true);

      setError(null);
      setIsEmptyData(false);

      try {
        const res = await getIndicesPeerCompare(
          region,
          index,
          tmpTab,
          sortcol,
          sortord,
          page,
          RESULTS_PER_PAGE,
          props.token || '',
        );
        setData(res.data);
        setTotal(res.total);
        setCurrency(res.data[0].currency);
        setIsEmptyData(res.total === 0);
        if (res.total === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'IndicesPeerComparisonWidget',
            data: null,
          });
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch recommendation data');
        console.error('Error fetching recommendation data:', err);
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      } finally {
        setIsLoading(false);
        setIsSorting(false);
      }
    },
    [page, props.token, tab, index, region, sortcol, sortord],
  );

  // Get Broker rating Class
  const getRecommendationClass = (recommendation: string) => {
    switch (recommendation) {
      case 'Strong Buy':
        return 'rec-strong-buy';
      case 'Buy':
        return 'rec-buy';
      case 'Hold':
        return 'rec-hold';
      case 'Reduce':
        return 'rec-reduce';
      case 'Sell':
        return 'rec-sell';
      default:
        return 'rec-hold';
    }
  };

  function getCompanyScoreClass(value: number): string {
    let scoreClass = '';
    if (value) {
      switch (value) {
        case 10:
          scoreClass = 'score-10';
          break;
        default:
          scoreClass = 'score';
      }
    }

    return scoreClass;
  }

  function getHeatmapStyle(value: number): any {
    for (const range of initialHeatmapColor) {
      if (value >= range.min && value < range.max) {
        return { backgroundColor: range.color, color: range.fontColor };
      }
    }
    return null;
  }

  function cutOffWords(text: string): string {
    const maxChars = isSmallScreen || isLargeScreen ? 50 : 22;
    const isLong = text.length > maxChars;
    const shown = !isLong ? text : text.slice(0, maxChars) + '…';
    return shown;
  }

  function changeSort(newSortCol: string) {
    setIsSorting(true);
    setPage(1);
    if (newSortCol !== sortcol) {
      setSortcol(newSortCol);
      setSortord('desc');
    } else {
      setSortord(sortord === 'desc' ? 'asc' : 'desc');
    }
  }

  function headerElement(colname: string, headername: string) {
    let sortIconPos = 'right';
    if (colname === 'ticker' || colname === 'name') sortIconPos = 'right';

    return colname === 'mktcap' ? (
      <div onClick={() => changeSort(colname)}>
        {headername}
        <span>({currency})</span>
        {getSortIcon(colname)}
      </div>
    ) : (
      <div onClick={() => changeSort(colname)}>
        {sortIconPos === 'left' ? getSortIcon(colname) : ''}
        {headername}
        {sortIconPos === 'right' ? getSortIcon(colname) : ''}
      </div>
    );
  }

  function changeSelectCol(selcol: string) {
    var apiCol = selcol;
    if (selcol === 'rcmdlabel') apiCol = 'rating';
    else if (selcol === 'peratio') apiCol = 'per';

    setSelcol(selcol);
    setSortcol(apiCol);
    setSortord('desc');
    setPage(1);
  }

  function getSortIcon(column: string): React.ReactNode {
    const isActive = sortcol === column;
    const Icon = isActive
      ? sortord === 'asc'
        ? SortAscIcon
        : SortIcon
      : SortableIcon;
    return (
      <Icon
        className={
          isActive
            ? sortord === 'asc'
              ? 'sort-icon-asc'
              : 'sort-icon-desc'
            : 'sort-icon-sortable'
        }
      />
    );
  }

  const handleOnClick = (symbol: string) => {
    if (props.onCallback && symbol) {
      let message = {
        data: { symbol: symbol },
        source: 'indicespeercomparison',
        type: WidgetMessageType.OnStockClick,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  };

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className={`ind-peer-com-container tab-${tab}`}>
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div>
            {(isSmallScreen || isLargeScreen) && (
              <div className="select-container">
                <CustomSelect
                  value={selcol}
                  onChange={(val) => changeSelectCol(val)}
                  options={[
                    { value: 'mktcap', label: 'Mkt cap' },
                    { value: 'peratio', label: 'PE ratio' },
                    { value: 'divyield', label: 'Div. yield' },
                    { value: 'score', label: 'Company score' },
                    ...(theme !== ThemeType.Potter
                      ? [{ value: 'rcmdlabel', label: 'Consensus' }]
                      : []),
                    { value: 'perf1M', label: '1 Month' },
                    { value: 'perf3M', label: '3 Months' },
                    { value: 'perf6M', label: '6 Months' },
                    { value: 'perf1Y', label: '1 Year' },
                    { value: 'beta', label: 'Beta' },
                    { value: 'avgvol30', label: 'Avg vol(30D)' },
                    { value: 'rsi', label: 'RSI(14)' },
                    { value: 'ma5', label: '5D MA' },
                    { value: 'ma25', label: '25D MA' },
                    { value: 'per', label: 'PER' },
                    { value: 'pbr', label: 'PBR' },
                    { value: 'roe', label: 'ROE' },
                    { value: 'overall', label: 'Overall' },
                    { value: 'fundamental', label: 'Fundamental' },
                    { value: 'earnings', label: 'Earnings' },
                    { value: 'valuation', label: 'Valuation' },
                    { value: 'performance', label: 'Performance' },
                    { value: 'risk', label: 'Risk' },
                    { value: 'targetprice', label: 'Target price' },
                    { value: 'rating', label: 'Broker rating' },
                    { value: 'numofbrokers', label: 'Number of brokers' },
                  ]}
                />
              </div>
            )}

            <table className="peer-com-table">
              <thead>
                {tab === 'summary' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    <th className="mktcap">
                      {headerElement('mktcap', 'Mkt cap')}
                    </th>
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="peratio">
                        {headerElement('per', 'PE ratio')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="divyield">
                        {headerElement('divyield', 'Div. yield')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="coscore">
                        {headerElement('score', 'Company score')}
                      </th>
                    )}
                    {!isSmallScreen &&
                      !isLargeScreen &&
                      theme !== ThemeType.Potter && (
                        <th className="consensus">
                          {headerElement('rating', 'Consensus')}
                        </th>
                      )}
                    <th></th>
                  </tr>
                ) : tab === 'performance' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="perf1M">
                        {headerElement('perf1M', '1 Month')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="perf3M">
                        {headerElement('perf3M', '3 Months')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="perf6M">
                        {headerElement('perf6M', '6 Months')}
                      </th>
                    )}
                    <th className="perf1Y">
                      {headerElement('perf1Y', '1 Year')}
                    </th>
                    <th></th>
                  </tr>
                ) : tab === 'technical' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="beta">{headerElement('beta', 'Beta')}</th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="avgvol30">
                        {headerElement('avgvol30', 'Avg vol(30D)')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="rsi">{headerElement('rsi', 'RSI(14)')}</th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="ma5d">{headerElement('ma5', '5D MA')}</th>
                    )}
                    <th className="ma25d">{headerElement('ma25', '25D MA')}</th>
                    <th></th>
                  </tr>
                ) : tab === 'fundamentals' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    <th className="mktcap">
                      {headerElement('mktcap', 'Mkt cap')}
                    </th>
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="per">{headerElement('per', 'PER')}</th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="pbr">{headerElement('pbr', 'PBR')}</th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="roe">{headerElement('roe', 'ROE')}</th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="divyield">
                        {headerElement('divyield', 'Div. Yield')}
                      </th>
                    )}
                    <th></th>
                  </tr>
                ) : tab === 'companyscore' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    <th className="overall">
                      {headerElement('overall', 'Overall')}
                    </th>
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="fundamental">
                        {headerElement('fundamental', 'Fundamental')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="earnings">
                        {headerElement('earnings', 'Earnings')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="valuation">
                        {headerElement('valuation', 'Valuation')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="performance">
                        {headerElement('performance', 'Performance')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="risk">{headerElement('risk', 'Risk')}</th>
                    )}
                    <th></th>
                  </tr>
                ) : tab === 'ratings' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">Code</th>
                        <th className="tickerName tickerName--name">Name</th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="targetprice">
                        {headerElement('targetprice', 'Target price')}
                      </th>
                    )}
                    <th className="brokerrating">
                      {headerElement('rating', 'Broker rating')}
                    </th>
                    {!isSmallScreen && !isLargeScreen && (
                      <th className="numofbrokers">
                        {headerElement('numofbrokers', 'Number of brokers')}
                      </th>
                    )}
                    <th></th>
                  </tr>
                ) : tab === 'narrow' ? (
                  <tr>
                    <th></th>
                    <th className="no">Rank</th>
                    <th className="ctry"></th>
                    {isSmallScreen && (
                      <>
                        <th className="tickerName tickerName--ticker">
                          <div>
                            <span onClick={() => changeSort('ticker')}>
                              Code{getSortIcon('ticker')}
                            </span>
                          </div>
                        </th>
                        <th className="tickerName tickerName--name">
                          <div>
                            <span onClick={() => changeSort('name')}>
                              Name{getSortIcon('name')}
                            </span>
                          </div>
                        </th>
                      </>
                    )}
                    {!isSmallScreen && (
                      <th className="ticker">
                        {headerElement('ticker', 'Code')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="name">{headerElement('name', 'Name')}</th>
                    )}
                    {!isSmallScreen && (
                      <th className="last">
                        {headerElement('last', 'Last price')}
                      </th>
                    )}
                    {!isSmallScreen && (
                      <th className="pctchng">
                        {headerElement('pctchng', '%Chg')}
                      </th>
                    )}
                    {selcol === 'mktcap' && (
                      <th className="mktcap">
                        {headerElement('mktcap', 'Mkt cap')}
                      </th>
                    )}
                    {selcol === 'peratio' && (
                      <th className="peratio">
                        {headerElement('per', 'PE ratio')}
                      </th>
                    )}
                    {selcol === 'divyield' && (
                      <th className="divyield">
                        {headerElement('divyield', 'Div. yield')}
                      </th>
                    )}
                    {selcol === 'score' && (
                      <th className="coscore">
                        {headerElement('score', 'Company score')}
                      </th>
                    )}
                    {selcol === 'rcmdlabel' && (
                      <th className="consensus">
                        {headerElement('rating', 'Consensus')}
                      </th>
                    )}
                    {selcol === 'perf1M' && (
                      <th className="perf1M">
                        {headerElement('perf1M', '1 Month')}
                      </th>
                    )}
                    {selcol === 'perf3M' && (
                      <th className="perf3M">
                        {headerElement('perf3M', '3 Months')}
                      </th>
                    )}
                    {selcol === 'perf6M' && (
                      <th className="perf6M">
                        {headerElement('perf6M', '6 Months')}
                      </th>
                    )}
                    {selcol === 'perf1Y' && (
                      <th className="perf1Y">
                        {headerElement('perf1Y', '1 Year')}
                      </th>
                    )}
                    {selcol === 'beta' && (
                      <th className="beta">{headerElement('beta', 'Beta')}</th>
                    )}
                    {selcol === 'avgvol30' && (
                      <th className="avgvol30">
                        {headerElement('avgvol30', 'Avg vol(30D)')}
                      </th>
                    )}
                    {selcol === 'rsi' && (
                      <th className="rsi">{headerElement('rsi', 'RSI(14)')}</th>
                    )}
                    {selcol === 'ma5' && (
                      <th className="ma5d">{headerElement('ma5', '5D MA')}</th>
                    )}
                    {selcol === 'ma25' && (
                      <th className="ma25d">
                        {headerElement('ma25', '25D MA')}
                      </th>
                    )}
                    {selcol === 'per' && (
                      <th className="per">{headerElement('per', 'PER')}</th>
                    )}
                    {selcol === 'pbr' && (
                      <th className="pbr">{headerElement('pbr', 'PBR')}</th>
                    )}
                    {selcol === 'roe' && (
                      <th className="roe">{headerElement('roe', 'ROE')}</th>
                    )}
                    {selcol === 'overall' && (
                      <th className="overall">
                        {headerElement('overall', 'Overall')}
                      </th>
                    )}
                    {selcol === 'fundamental' && (
                      <th className="fundamental">
                        {headerElement('fundamental', 'Fundamental')}
                      </th>
                    )}
                    {selcol === 'earnings' && (
                      <th className="earnings">
                        {headerElement('earnings', 'Earnings')}
                      </th>
                    )}
                    {selcol === 'valuation' && (
                      <th className="valuation">
                        {headerElement('valuation', 'Valuation')}
                      </th>
                    )}
                    {selcol === 'performance' && (
                      <th className="performance">
                        {headerElement('performance', 'Performance')}
                      </th>
                    )}
                    {selcol === 'risk' && (
                      <th className="risk">{headerElement('risk', 'Risk')}</th>
                    )}
                    {selcol === 'targetprice' && (
                      <th className="targetprice">
                        {headerElement('targetprice', 'Target price')}
                      </th>
                    )}
                    {selcol === 'rating' && (
                      <th className="brokerrating">
                        {headerElement('rating', 'Broker rating')}
                      </th>
                    )}
                    {selcol === 'numofbrokers' && (
                      <th className="numofbrokers">
                        {headerElement('numofbrokers', 'No. of brokers')}
                      </th>
                    )}
                    <th className="lastcolumn"></th>
                  </tr>
                ) : null}
              </thead>

              <div className="ind-peer-com-table-spacer"></div>

              <tbody>
                {data.map((item, index) => {
                  return tab === 'summary' ? (
                    <tr key={index}>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className={
                              props.onCallback
                                ? 'ticker link-enabled'
                                : 'ticker'
                            }
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={
                            props.onCallback ? 'ticker link-enabled' : 'ticker'
                          }
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className={classNames('name')}>
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td className="mktcap">
                        {item.mktcap ? (
                          <div>
                            {item.mktcap
                              ? formatKMBT(item.mktcap.toString(), 3, 3, false)
                                  .value
                              : '-'}
                            <span>
                              {item.mktcap
                                ? formatKMBT(
                                    item.mktcap.toString(),
                                    3,
                                    3,
                                    false,
                                  ).suffix
                                : '-'}
                            </span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="peratio">
                          {formatNumber(item.per, 2, 2)}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="divyield">
                          {formatNumber(
                            item.divyield,
                            2,
                            2,
                            false,
                            false,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="coscore">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.score)} alignRight`}
                            >
                              {item.score ? item.score.toFixed(0) : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen &&
                        !isLargeScreen &&
                        theme !== ThemeType.Potter && (
                          <td className="div-consensus">
                            <div className="alignRight">
                              <span
                                className={`${getRecommendationClass(item.rcmdlabel)}`}
                              >
                                {item.rcmdlabel ? item.rcmdlabel : '-'}
                              </span>
                            </div>
                          </td>
                        )}
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'performance' ? (
                    <tr key={index} className="per-whiteBorder">
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className="ticker"
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td
                          className="perf1M"
                          style={getHeatmapStyle(item.perf1M)}
                        >
                          {formatNumber(
                            item.perf1M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td
                          className="perf3M"
                          style={getHeatmapStyle(item.perf3M)}
                        >
                          {formatNumber(
                            item.perf3M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td
                          className="perf6M"
                          style={getHeatmapStyle(item.perf6M)}
                        >
                          {formatNumber(
                            item.perf6M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td
                        className={`perf1Y ${isSmallScreen || isLargeScreen ? getUpDownClass(item.perf1Y) : ''}`}
                        style={{
                          ...(!isSmallScreen && !isLargeScreen
                            ? getHeatmapStyle(item.perf1Y)
                            : ''),
                        }}
                      >
                        {formatNumber(
                          item.perf1Y,
                          2,
                          2,
                          false,
                          true,
                          false,
                          '%',
                        )}
                      </td>
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'technical' ? (
                    <tr key={index}>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className="ticker"
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="beta">
                          {formatNumber(item.beta, 2, 2)}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="avgvol30">
                          {formatNumber(item.avgvol30, 2, 2)}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="rsi">{formatNumber(item.rsi, 2, 2)}</td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className={`ma5d ${getUpDownClass(item.ma5)}`}>
                          {formatNumber(
                            item.ma5,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td className={`ma25d ${getUpDownClass(item.ma25)}`}>
                        {formatNumber(item.ma25, 2, 2, false, true, false, '%')}
                      </td>
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'fundamentals' ? (
                    <tr key={index}>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className="ticker"
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td className="mktcap">
                        {item.mktcap ? (
                          <div>
                            {
                              formatKMBT(item.mktcap.toString(), 3, 3, false)
                                .value
                            }
                            <span>
                              {
                                formatKMBT(item.mktcap.toString(), 3, 3, false)
                                  .suffix
                              }
                            </span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="per">{formatNumber(item.per, 2, 2)}</td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="pbr">{formatNumber(item.pbr, 2, 2)}</td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="roe">
                          {formatNumber(
                            item.roe,
                            2,
                            2,
                            false,
                            false,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="divyield">
                          {formatNumber(
                            item.divyield,
                            2,
                            2,
                            false,
                            false,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'companyscore' ? (
                    <tr key={index}>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className="ticker"
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      <td className="overall">
                        <div className="alignRight">
                          <span
                            className={`${getCompanyScoreClass(item.overall)} alignRight`}
                          >
                            {item.overall ? item.overall : '-'}
                          </span>
                        </div>
                      </td>
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="fundamental">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.fundamental)} alignRight`}
                            >
                              {item.fundamental ? item.fundamental : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="earnings">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.earnings)} alignRight`}
                            >
                              {item.earnings ? item.earnings : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="valuation">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.valuation)} alignRight`}
                            >
                              {item.valuation ? item.valuation : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="performance">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.performance)} alignRight`}
                            >
                              {item.performance ? item.performance : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="risk">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.risk)} alignRight`}
                            >
                              {item.risk ? item.risk : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'ratings' ? (
                    <tr>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <span
                            className="ticker"
                            onClick={() => handleOnClick(item.bell_code)}
                          >
                            {item.ticker}
                          </span>
                          <br />
                          <span className="name">
                            {setToolTips(item, 'name')}
                          </span>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="targetprice">
                          {formatNumber(
                            item.targetprice,
                            item.dp,
                            item.dp,
                            false,
                            false,
                            false,
                            '',
                          )}
                        </td>
                      )}
                      <td className="brokerrating">
                        <div className="alignRight">
                          <span
                            className={`${getRecommendationClass(item.rcmdlabel)}`}
                          >
                            {item.rcmdlabel ? item.rcmdlabel : '-'}
                          </span>
                        </div>
                      </td>
                      {!isSmallScreen && !isLargeScreen && (
                        <td className="numofbrokers">
                          {item.ratinglist && item.numofbrokers > 0 ? (
                            <div
                              style={{
                                backgroundColor: 'black',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                }}
                              >
                                {item.ratinglist.map((item: any) => (
                                  <div
                                    className={getRecommendationClass(item.key)}
                                    style={{
                                      flex: item.value || 0.5,
                                      padding: '4px',
                                    }}
                                  >
                                    {item.value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                textAlign: 'center',
                                fontWeight: '500',
                                backgroundColor: '#E0E0E0',
                                color: '#1E1F2F',
                                padding: '4px',
                              }}
                            >
                              -
                            </div>
                          )}
                        </td>
                      )}
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : tab === 'narrow' ? (
                    <tr key={index}>
                      <td className="firstcolumn"></td>
                      <td className="no">
                        {(page - 1) * RESULTS_PER_PAGE + index + 1}
                      </td>
                      <td className="ctry">
                        {item.cc ? getFlagSvg(item.cc) : '-'}
                      </td>
                      {isSmallScreen && (
                        <td className="tickerName" colSpan={2}>
                          <div className="tickerNameSmallScreen">
                            <span
                              className="ticker"
                              onClick={() => handleOnClick(item.bell_code)}
                            >
                              {item.ticker}
                            </span>
                            <span className="name">
                              {setToolTips(item, 'name')}
                            </span>
                          </div>
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="ticker"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.ticker ? item.ticker : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className="name"
                          onClick={() => handleOnClick(item.bell_code)}
                        >
                          {item.name ? setToolTips(item, 'name') : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td className="last">
                          {item.last
                            ? formatNumber(
                                item.last.toString(),
                                item.dp,
                                item.dp,
                                true,
                                false,
                                false,
                                '',
                              )
                            : '-'}
                        </td>
                      )}
                      {!isSmallScreen && (
                        <td
                          className={`pctchng ${getUpDownClass(item.pctchng)}`}
                        >
                          {formatNumber(
                            item.pctchng,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'mktcap' && (
                        <td className="mktcap">
                          {item.mktcap ? (
                            <div>
                              {item.mktcap
                                ? formatKMBT(
                                    item.mktcap.toString(),
                                    3,
                                    3,
                                    false,
                                  ).value
                                : '-'}
                              <span>
                                {item.mktcap
                                  ? formatKMBT(
                                      item.mktcap.toString(),
                                      3,
                                      3,
                                      false,
                                    ).suffix
                                  : '-'}
                              </span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                      )}
                      {selcol === 'peratio' && (
                        <td className="peratio">
                          {formatNumber(item.per, 2, 2)}
                        </td>
                      )}
                      {selcol === 'divyield' && (
                        <td className="divyield">
                          {formatNumber(
                            item.divyield,
                            2,
                            2,
                            false,
                            false,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'score' && (
                        <td className="coscore">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.score)} alignRight`}
                            >
                              {item.score ? item.score.toFixed(0) : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'rcmdlabel' && (
                        <td className="div-consensus">
                          <div className="alignRight">
                            <span
                              className={`${getRecommendationClass(item.rcmdlabel)}`}
                            >
                              {item.rcmdlabel ? item.rcmdlabel : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'perf1M' && (
                        <td className={`perf1M ${getUpDownClass(item.perf1M)}`}>
                          {formatNumber(
                            item.perf1M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'perf3M' && (
                        <td className={`perf3M ${getUpDownClass(item.perf3M)}`}>
                          {formatNumber(
                            item.perf3M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'perf6M' && (
                        <td className={`perf6M ${getUpDownClass(item.perf6M)}`}>
                          {formatNumber(
                            item.perf6M,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'perf1Y' && (
                        <td className={`perf1Y ${getUpDownClass(item.perf1Y)}`}>
                          {formatNumber(
                            item.perf1Y,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'beta' && (
                        <td className="beta">
                          {formatNumber(item.beta, 2, 2)}
                        </td>
                      )}
                      {selcol === 'avgvol30' && (
                        <td className="avgvol30">
                          {formatNumber(item.avgvol30, 2, 2)}
                        </td>
                      )}
                      {selcol === 'rsi' && (
                        <td className="rsi">{formatNumber(item.rsi, 2, 2)}</td>
                      )}
                      {selcol === 'ma5' && (
                        <td className={`ma5d ${getUpDownClass(item.ma5)}`}>
                          {formatNumber(
                            item.ma5,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'ma25' && (
                        <td className={`ma25d ${getUpDownClass(item.ma25)}`}>
                          {formatNumber(
                            item.ma25,
                            2,
                            2,
                            false,
                            true,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'per' && (
                        <td className="per">{formatNumber(item.per, 2, 2)}</td>
                      )}
                      {selcol === 'pbr' && (
                        <td className="pbr">{formatNumber(item.pbr, 2, 2)}</td>
                      )}
                      {selcol === 'roe' && (
                        <td className={`roe ${getUpDownClass(item.roe)}`}>
                          {formatNumber(
                            item.roe,
                            2,
                            2,
                            false,
                            false,
                            false,
                            '%',
                          )}
                        </td>
                      )}
                      {selcol === 'overall' && (
                        <td className="overall">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.overall)} alignRight`}
                            >
                              {item.overall ? item.overall : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'fundamental' && (
                        <td className="fundamental">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.fundamental)} alignRight`}
                            >
                              {item.fundamental ? item.fundamental : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'earnings' && (
                        <td className="earnings">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.earnings)} alignRight`}
                            >
                              {item.earnings ? item.earnings : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'valuation' && (
                        <td className="valuation">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.valuation)} alignRight`}
                            >
                              {item.valuation ? item.valuation : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'performance' && (
                        <td className="performance">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.performance)} alignRight`}
                            >
                              {item.performance ? item.performance : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'risk' && (
                        <td className="risk">
                          <div className="alignRight">
                            <span
                              className={`${getCompanyScoreClass(item.risk)} alignRight`}
                            >
                              {item.risk ? item.risk : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'targetprice' && (
                        <td className={`targetprice`}>
                          {formatNumber(
                            item.targetprice,
                            item.dp,
                            item.dp,
                            false,
                            false,
                            false,
                            '',
                          )}
                        </td>
                      )}
                      {selcol === 'rating' && (
                        <td className="brokerrating">
                          <div className="alignRight">
                            <span
                              className={`${getRecommendationClass(item.rcmdlabel)}`}
                            >
                              {item.rcmdlabel ? item.rcmdlabel : '-'}
                            </span>
                          </div>
                        </td>
                      )}
                      {selcol === 'numofbrokers' && (
                        <td className="numofbrokers">
                          {item.ratinglist && item.numofbrokers > 0 ? (
                            <div
                              style={{
                                backgroundColor: 'black',
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                }}
                              >
                                {item.ratinglist.map((item: any) => (
                                  <div
                                    className={getRecommendationClass(item.key)}
                                    style={{
                                      flex: item.value || 0.5,
                                      padding: '4px',
                                    }}
                                  >
                                    {item.value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                textAlign: 'center',
                                fontWeight: '500',
                                backgroundColor: '#E0E0E0',
                                color: '#1E1F2F',
                                padding: '4px',
                              }}
                            >
                              -
                            </div>
                          )}
                        </td>
                      )}
                      <td className="lastcolumn"></td>
                    </tr>
                  ) : null;
                })}
              </tbody>
            </table>

            <div className="peer-com-whitespace"></div>
            {
              <Pagination
                pagination={pagination}
                onPageClick={(targetPage: number) => {
                  setPage(targetPage);
                }}
                haveLastPage={true}
              />
            }

            <CommonTooltip
              ref={peercomTooltipRef}
              content={tooltip.content}
              show={tooltip.show}
              x={tooltip.x}
              y={tooltip.y}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IndicesPeerComparisonWidget;
